import { inject } from '@angular/core';
import type { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';

const AUTH_PREFIX = `${environment.apiUrl}/auth/`;
const RUTAS_AUTH_PUBLICAS = ['login', 'login/2fa', 'registro', 'refresh', 'logout', 'forgot-password', 'reset-password'];

// Bearer solo hacia nuestra API (nunca a terceros, p. ej. imágenes de SHEIN). La cookie
// vitrina_refresh solo viaja a /auth/* (Path=/api/v1/auth), así que withCredentials se pone ahí.
function adjuntar(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!req.url.startsWith(environment.apiUrl)) return req;
  return req.clone({
    withCredentials: req.url.startsWith(AUTH_PREFIX) ? true : req.withCredentials,
    setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// Orden en app.config.ts: [errorInterceptor, authInterceptor, ...]. La respuesta recorre los
// interceptores al revés, así que aquí llega el HttpErrorResponse crudo (patrón VendIA).
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const token = auth.accessToken();

  // Un 401 en las rutas de /auth SIN sesión (login, registro, refresh, logout, recuperar) es un
  // error de negocio (credenciales, token 2FA, refresh vencido), nunca "sesión expirada": no se
  // reintenta (evita el bucle que VendIA documenta con /auth/logout). Las que usan la sesión
  // (/auth/me, /auth/2fa/*, /auth/cambiar-password, /auth/logout/all) sí renuevan el token.
  const esAuthPublico = req.url.startsWith(AUTH_PREFIX) && RUTAS_AUTH_PUBLICAS.some((r) => req.url.split('?')[0] === `${AUTH_PREFIX}${r}`);

  return next(adjuntar(req, token)).pipe(
    catchError((error: unknown): Observable<HttpEvent<unknown>> => {
      if (esAuthPublico || !token || !(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      // 401 → /auth/refresh → reintento UNA vez. Solo se cierra la sesión si el REFRESH dice 401
      // (sesión vencida de verdad). Si falla el reintento (p. ej. otra pestaña renovó en ese
      // instante), el error va a la pantalla, pero la sesión sigue (D-165).
      return auth.refreshToken().pipe(
        catchError((errRefresh: unknown) => {
          // El refresh pasa por error.interceptor: llega normalizado ({ status, code, message })
          if ((errRefresh as { status?: number } | null)?.status === 401) {
            auth.logout();
            router.navigate(['/login'], { queryParams: { volver: router.url } });
          }
          return throwError(() => error);
        }),
        switchMap((nuevo) => next(adjuntar(req, nuevo))),
      );
    }),
  );
};
